'use client'

import dynamic from 'next/dynamic'
import { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Fade, Portal, Typography, Accordion, AccordionSummary, AccordionDetails, useTheme } from '@mui/material'

// Types
import type { NavDropdownProps } from '@/types/components/organisms/mega-menu/nav-dropdown'

// Helpers
import { CLOSE_DELAY, backdropSx, dimSiblingsSx, triggerSx } from '@/helpers/mega-menu'

// Utils
import { localizeHref } from '@/utils/theme/nav-menu'

// Components
// The arrow is imported directly (not with dynamic) so the button never suspends and shifts the header
import ArrowDownIconAtom from '@/components/atoms/icon/two-tone/arrow-down'
const MegaMenuItemMolecule = dynamic(() => import('@/components/molecules/mega-menu/item'))
const MegaMenuMobileItemMolecule = dynamic(() => import('@/components/molecules/mega-menu/mobile-item'))

const PANEL_WIDTH = 280
const PAGE_GUTTER = 16

// Single-column header dropdown (Resources, Our Story): same behavior and style as the Solutions mega menu
const NavDropdownOrganism = (props: NavDropdownProps) => {
	// Props
	const { lng, group, top, variant } = props

	// Variables
	const theme = useTheme()
	const isRtl = theme.direction === 'rtl'
	const buttonRef = useRef<HTMLButtonElement | null>(null)
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
	const [open, setOpen] = useState(false)
	// Panel distance from the left edge of the viewport (physical, independent of text direction)
	const [left, setLeft] = useState(0)

	// Callbacks
	const show = useCallback(() => {
		if (closeTimer.current) clearTimeout(closeTimer.current)
		// The panel opens under its button: aligned to the button's right edge in RTL, its left edge otherwise
		const rect = buttonRef.current?.getBoundingClientRect()
		if (rect) {
			const wanted = isRtl ? rect.right - PANEL_WIDTH : rect.left
			setLeft(Math.min(Math.max(PAGE_GUTTER, wanted), window.innerWidth - PANEL_WIDTH - PAGE_GUTTER))
		}
		setOpen(true)
	}, [isRtl])

	const hide = useCallback(() => {
		if (closeTimer.current) clearTimeout(closeTimer.current)
		closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY)
	}, [])

	useEffect(() => {
		if (!open) return
		const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [open])

	useEffect(() => () => void (closeTimer.current && clearTimeout(closeTimer.current)), [])

	if (variant === 'mobile') {
		return (
			<Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&::before': { display: 'none' } }}>
				<AccordionSummary expandIcon={<ArrowDownIconAtom />} sx={{ px: 1, '& .MuiTypography-root': { fontSize: 16, fontWeight: 600 } }}>
					<Typography>{group.title}</Typography>
				</AccordionSummary>

				<AccordionDetails sx={{ px: 1 }}>
					{group.items.map(item => (
						<MegaMenuMobileItemMolecule key={item.title} href={localizeHref(lng, item.href)} icon={item.icon} title={item.title} />
					))}
				</AccordionDetails>
			</Accordion>
		)
	}

	return (
		<Box onMouseEnter={show} onMouseLeave={hide}>
			<Button
				ref={buttonRef}
				aria-haspopup="true"
				aria-expanded={open}
				onClick={show}
				endIcon={<ArrowDownIconAtom sx={{ fontSize: '16px !important', transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none' }} />}
				sx={triggerSx(open)}
			>
				{group.title}
			</Button>

			<Suspense fallback={null}>
				<Portal>
					<Fade in={open} timeout={200} unmountOnExit>
						<Box onClick={() => setOpen(false)} sx={backdropSx(top)} />
					</Fade>

					<Fade in={open} timeout={200} unmountOnExit>
						<Box
							onMouseEnter={show}
							onMouseLeave={hide}
							// left is set with style, not sx: the RTL style plugin swaps left/right and pushed the panel away from its button
							style={{ left }}
							sx={{ position: 'fixed', top: `${top}px`, zIndex: t => t.zIndex.appBar - 1, pt: 1 }}
						>
							<Box
								role="menu"
								aria-label={group.title}
								sx={{
									width: PANEL_WIDTH,
									p: 2,
									bgcolor: 'common.white',
									borderRadius: 3,
									boxShadow: '0 24px 48px -12px rgba(23, 23, 23, 0.18)',
									...dimSiblingsSx
								}}
							>
								{group.items.map(item => (
									<MegaMenuItemMolecule
										key={item.title}
										href={localizeHref(lng, item.href)}
										icon={item.icon}
										title={item.title}
										compact
										onClick={() => setOpen(false)}
									/>
								))}
							</Box>
						</Box>
					</Fade>
				</Portal>
			</Suspense>
		</Box>
	)
}

export default NavDropdownOrganism
